import { useLayoutEffect, useRef } from 'react';

import {
  useShortlistActions,
  useShortlistScrollY,
  useShortlistShowAll,
} from '@/modules/public-shortlist/store';

export function useShortlistScrollPersistence(
  shortlistKey: string,
  enabled: boolean,
) {
  const scrollY = useShortlistScrollY(shortlistKey);
  const showAll = useShortlistShowAll(shortlistKey);
  const { setScrollY, setCtaRevealed } = useShortlistActions();
  const shouldRestoreScroll = useRef(false);

  useLayoutEffect(() => {
    if (!enabled || !shortlistKey) {
      return;
    }

    shouldRestoreScroll.current = scrollY > 0;
  }, [enabled, scrollY, shortlistKey]);

  useLayoutEffect(() => {
    if (!enabled || !shortlistKey) {
      return;
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollY(shortlistKey, currentScrollY);

      if (currentScrollY > 0) {
        setCtaRevealed(shortlistKey, true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [enabled, setCtaRevealed, setScrollY, shortlistKey]);

  useLayoutEffect(() => {
    if (
      !enabled ||
      !shortlistKey ||
      !shouldRestoreScroll.current ||
      scrollY <= 0
    ) {
      return;
    }

    let frame = 0;
    let innerFrame = 0;
    let timeout = 0;

    const restore = () => {
      window.scrollTo(0, scrollY);
      setCtaRevealed(shortlistKey, true);
      shouldRestoreScroll.current = false;
    };

    frame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(restore);
    });

    timeout = window.setTimeout(restore, 150);

    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(innerFrame);
      window.clearTimeout(timeout);
    };
  }, [enabled, scrollY, showAll, setCtaRevealed, shortlistKey]);
}
